import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FiGithub, FiLinkedin, FiMail, FiMapPin, FiSend, FiCheck, FiAlertCircle, FiExternalLink } from 'react-icons/fi';
import { SiFiverr, SiUpwork } from 'react-icons/si';
import RevealHeading from '../common/RevealHeading';
import useParallax from '../common/useParallax';

const ContactSection = () => {
  const { ref: parallaxRef, y: parallaxY } = useParallax(60);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [status, setStatus] = useState('idle'); // idle | sending | success | error
  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }
    if (!formData.subject.trim()) newErrors.subject = 'Subject is required';
    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    } else if (formData.message.trim().length < 10) {
      newErrors.message = 'Message should be at least 10 characters';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear error on change
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setStatus('sending');

    try {
      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          access_key: 'eebc8e6e-323a-497a-977e-f5a4db81f146',
          name: formData.name,
          email: formData.email,
          subject: `[Portfolio] ${formData.subject}`,
          message: formData.message,
          from_name: 'Portfolio Contact Form',
        }),
      });

      const result = await response.json();

      if (result.success) {
        setStatus('success');
        setFormData({ name: '', email: '', subject: '', message: '' });
        setTimeout(() => setStatus('idle'), 5000);
      } else {
        setStatus('error');
        setTimeout(() => setStatus('idle'), 5000);
      }
    } catch (err) {
      setStatus('error');
      setTimeout(() => setStatus('idle'), 5000);
    }
  };

  const contactLinks = [
    {
      label: 'Email',
      value: 'harixmughal65@gmail.com',
      href: 'mailto:harixmughal65@gmail.com',
      icon: FiMail,
    },
    {
      label: 'GitHub',
      value: '@HARISMUGHAL',
      href: 'https://github.com/HARISMUGHAL',
      icon: FiGithub,
    },
    {
      label: 'LinkedIn',
      value: 'Haris Mughal',
      href: 'https://www.linkedin.com/in/haris-mughal-183706341/',
      icon: FiLinkedin,
    },
    {
      label: 'Location',
      value: 'Islamabad, Pakistan',
      href: null,
      icon: FiMapPin,
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1, delayChildren: 0.2 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
  };

  return (
    <section id="contact" ref={parallaxRef} className="py-20 lg:py-28 relative overflow-hidden">
      {/* Background Elements */}
      <motion.div
        style={{ y: parallaxY }}
        animate={{ opacity: [0.2, 0.4, 0.2] }}
        transition={{ duration: 6, repeat: Infinity }}
        className="absolute -bottom-40 -right-40 w-80 h-80 bg-accent-500 rounded-full blur-3xl opacity-10"
      />

      <div className="container-custom relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <RevealHeading text="Get In" accent="Touch" className="text-4xl lg:text-5xl font-bold text-white mb-4" />
          <p className="text-white/60 text-lg max-w-2xl mx-auto">
            Have a project or opportunity to discuss? Send me a message and I'll get back to you within 24 hours.
          </p>
        </motion.div>

        {/* Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {/* Contact Info */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="lg:col-span-1 space-y-6"
          >
            {contactLinks.map((link, idx) => {
              const Icon = link.icon;
              const WrapperTag = link.href ? 'a' : 'div';
              const wrapperProps = link.href
                ? {
                    href: link.href,
                    target: link.href.startsWith('http') ? '_blank' : '_self',
                    rel: 'noopener noreferrer',
                  }
                : {};
              return (
                <motion.div key={idx} variants={itemVariants}>
                  <WrapperTag
                    {...wrapperProps}
                    className="flex items-start gap-4 p-4 rounded-lg bg-dark-800/30 border border-primary-500/10 hover:border-primary-500/30 transition-all duration-300 group block"
                  >
                    <div className="mt-1 p-3 rounded-lg bg-primary-500/20 text-primary-500 group-hover:bg-primary-500 group-hover:text-dark-900 transition-all duration-300">
                      <Icon className="text-xl" />
                    </div>
                    <div>
                      <p className="text-sm text-white/50 mb-1">{link.label}</p>
                      <p className="text-white font-medium group-hover:text-primary-500 transition-colors">
                        {link.value}
                      </p>
                    </div>
                  </WrapperTag>
                </motion.div>
              );
            })}
          </motion.div>

          {/* Contact Form */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="lg:col-span-2"
          >
            <form onSubmit={handleSubmit} className="space-y-5" noValidate>
              <motion.div variants={itemVariants}>
                <label className="block text-sm font-medium text-white mb-2">
                  Full Name
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className={`w-full px-4 py-3 rounded-lg bg-dark-800/50 border ${
                    errors.name ? 'border-red-500' : 'border-primary-500/20'
                  } text-white placeholder-white/40 focus:border-primary-500 focus:outline-none transition-all duration-300`}
                  placeholder="e.g. John Doe"
                />
                {errors.name && (
                  <p className="mt-1 text-sm text-red-400 flex items-center gap-1">
                    <FiAlertCircle className="text-xs" /> {errors.name}
                  </p>
                )}
              </motion.div>

              <motion.div variants={itemVariants}>
                <label className="block text-sm font-medium text-white mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={`w-full px-4 py-3 rounded-lg bg-dark-800/50 border ${
                    errors.email ? 'border-red-500' : 'border-primary-500/20'
                  } text-white placeholder-white/40 focus:border-primary-500 focus:outline-none transition-all duration-300`}
                  placeholder="e.g. john@company.com"
                />
                {errors.email && (
                  <p className="mt-1 text-sm text-red-400 flex items-center gap-1">
                    <FiAlertCircle className="text-xs" /> {errors.email}
                  </p>
                )}
              </motion.div>

              <motion.div variants={itemVariants}>
                <label className="block text-sm font-medium text-white mb-2">
                  Subject
                </label>
                <input
                  type="text"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  className={`w-full px-4 py-3 rounded-lg bg-dark-800/50 border ${
                    errors.subject ? 'border-red-500' : 'border-primary-500/20'
                  } text-white placeholder-white/40 focus:border-primary-500 focus:outline-none transition-all duration-300`}
                  placeholder="e.g. Project Inquiry, Freelance Work, Collaboration"
                />
                {errors.subject && (
                  <p className="mt-1 text-sm text-red-400 flex items-center gap-1">
                    <FiAlertCircle className="text-xs" /> {errors.subject}
                  </p>
                )}
              </motion.div>

              <motion.div variants={itemVariants}>
                <label className="block text-sm font-medium text-white mb-2">
                  Message
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows="5"
                  className={`w-full px-4 py-3 rounded-lg bg-dark-800/50 border ${
                    errors.message ? 'border-red-500' : 'border-primary-500/20'
                  } text-white placeholder-white/40 focus:border-primary-500 focus:outline-none transition-all duration-300 resize-none`}
                  placeholder="Tell me about your project, timeline, and budget..."
                />
                {errors.message && (
                  <p className="mt-1 text-sm text-red-400 flex items-center gap-1">
                    <FiAlertCircle className="text-xs" /> {errors.message}
                  </p>
                )}
              </motion.div>

              {/* Submit Button */}
              <motion.button
                variants={itemVariants}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                type="submit"
                disabled={status === 'sending'}
                className={`w-full px-6 py-3 rounded-lg font-bold text-lg transition-all duration-300 flex items-center justify-center gap-2 ${
                  status === 'sending'
                    ? 'bg-dark-600 text-white/50 cursor-not-allowed'
                    : status === 'success'
                    ? 'bg-green-600 text-white'
                    : status === 'error'
                    ? 'bg-red-600 text-white'
                    : 'bg-gradient-to-r from-primary-500 to-accent-500 text-dark-900 hover:shadow-glow-primary'
                }`}
              >
                {status === 'sending' && (
                  <>
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Sending...
                  </>
                )}
                {status === 'success' && (
                  <>
                    <FiCheck className="text-xl" />
                    Message Sent Successfully
                  </>
                )}
                {status === 'error' && (
                  <>
                    <FiAlertCircle className="text-xl" />
                    Failed to Send — Please Try Again
                  </>
                )}
                {status === 'idle' && (
                  <>
                    <FiSend className="text-lg" />
                    Send Message
                  </>
                )}
              </motion.button>
            </form>
          </motion.div>
        </div>

        {/* Quick Links */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="mt-16 pt-16 border-t border-primary-500/10 text-center"
        >
          <p className="text-white/60 mb-6">
            You can also reach me directly on these platforms
          </p>
          <div className="flex justify-center gap-4 flex-wrap">
            <motion.a
              href="https://github.com/HARISMUGHAL"
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.1, y: -5 }}
              className="p-4 rounded-lg bg-dark-800/30 border border-primary-500/20 text-primary-500 hover:bg-primary-500 hover:text-dark-900 transition-all duration-300 inline-flex items-center gap-2"
            >
              <FiGithub className="text-xl" />
              <span className="hidden sm:inline">GitHub</span>
            </motion.a>
            <motion.a
              href="https://www.linkedin.com/in/haris-mughal-183706341/"
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.1, y: -5 }}
              className="p-4 rounded-lg bg-dark-800/30 border border-accent-500/20 text-accent-500 hover:bg-accent-500 hover:text-dark-900 transition-all duration-300 inline-flex items-center gap-2"
            >
              <FiLinkedin className="text-xl" />
              <span className="hidden sm:inline">LinkedIn</span>
            </motion.a>
            <motion.a
              href="mailto:harixmughal65@gmail.com"
              whileHover={{ scale: 1.1, y: -5 }}
              className="p-4 rounded-lg bg-dark-800/30 border border-primary-500/20 text-primary-500 hover:bg-primary-500 hover:text-dark-900 transition-all duration-300 inline-flex items-center gap-2"
            >
              <FiMail className="text-xl" />
              <span className="hidden sm:inline">Email</span>
            </motion.a>
            <motion.a
              href="https://www.fiverr.com/s/aexDjWp"
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.1, y: -5 }}
              className="p-4 rounded-lg bg-dark-800/30 border border-[#1dbf73]/20 text-[#1dbf73] hover:bg-[#1dbf73] hover:text-dark-900 transition-all duration-300 inline-flex items-center gap-2"
            >
              <SiFiverr className="text-2xl" />
              <span className="hidden sm:inline">Fiverr</span>
            </motion.a>
            <motion.a
              href="https://www.upwork.com/freelancers/~016b95942fb99caccf"
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.1, y: -5 }}
              className="p-4 rounded-lg bg-dark-800/30 border border-[#14a800]/20 text-[#14a800] hover:bg-[#14a800] hover:text-dark-900 transition-all duration-300 inline-flex items-center gap-2"
            >
              <SiUpwork className="text-xl" />
              <span className="hidden sm:inline">Upwork</span>
            </motion.a>
            <motion.a
              href="https://zynqoraedge.com"
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.1, y: -5 }}
              className="p-4 rounded-lg bg-dark-800/30 border border-accent-500/20 text-accent-500 hover:bg-accent-500 hover:text-dark-900 transition-all duration-300 inline-flex items-center gap-2"
            >
              <FiExternalLink className="text-xl" />
              <span className="hidden sm:inline">Zynqora Edge</span>
            </motion.a>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ContactSection;
